import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  LogOut,
  BookOpen,
  Upload,
  Trash2,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Database,
  ArrowRight,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminJobImportPage() {
  const { authAxios, logout } = useAuth();
  
  const [currentStatus, setCurrentStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null); // { columns, rows, stats }
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importProgress, setImportProgress] = useState(null); // { current, total, pct }

  // ─── Canonical Column Map (case-insensitive) ──────────────────────────────
  // Any capitalisation/spacing variation maps to the canonical name
  const CANONICAL_COLUMNS = {
    'job title': 'Job title',
    'job type': 'Job type',
    'company name': 'Company Name',
    'company url': 'Company URL',
    'company logo': 'Company logo',
    'job location': 'Job location',
    'office location': 'Office location',
    'location limits': 'Location Limits',
    'description': 'Description',
    'apply url': 'Apply URL',
    'apply email': 'Apply email',
    'salary min': 'Salary min',
    'salary maximum': 'Salary maximum',
    'salary currency': 'Salary currency',
    'salary schedule': 'Salary schedule',
    'post length': 'Post length',
    'post state': 'Post state',
    'date posted': 'Date posted',
    'category name': 'Category name',
  };

  const REQUIRED_COLUMNS = new Set([
    'Job title', 'Company Name', 'Description', 'Apply URL', 'Date posted', 'Category name',
  ]);

  const OPTIONAL_COLUMNS = new Set([
    'Apply email', 'Company logo', 'Location Limits',
    'Company URL', 'Office location', 'Salary currency', 'Salary schedule',
    'Post length', 'Post state',
  ]);

  const ALL_CANONICAL = new Set(Object.values(CANONICAL_COLUMNS));

  const canonicalise = (h) => CANONICAL_COLUMNS[h.toLowerCase().replace(/\s+/g, ' ').trim()] || null;

  const downloadTemplate = () => {
    const headers = Object.values(CANONICAL_COLUMNS);
    const sample = [
      'Director, Oncology Clinical Operations', 'Full-time', 'Pfizer',
      'https://pfizer.com', '', 'Remote', 'New York NY', '',
      'Lead clinical operations for Phase 2/3 oncology trials...',
      'https://pfizer.com/careers/apply/12345', '', '180000', '250000',
      'USD', 'Annual', '', 'Published', '01/15/2026', 'Clinical Research',
    ];
    const csv = [headers.join(','), sample.join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'varyynt-jobs-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  // ─── Client-side CSV Parser (state-aware — handles multi-line quoted fields) ─
  const parseCSV = (text) => {
    // Process character-by-character to correctly handle newlines inside quoted fields
    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const allRows = [];
    let currentRow = [];
    let currentField = '';
    let inQuotes = false;

    for (let i = 0; i < normalized.length; i++) {
      const ch = normalized[i];
      if (inQuotes) {
        if (ch === '"') {
          if (normalized[i + 1] === '"') { currentField += '"'; i++; } // escaped quote
          else { inQuotes = false; }                                    // end of quoted field
        } else {
          currentField += ch; // include newlines that are inside quotes
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
        } else if (ch === ',') {
          currentRow.push(currentField.trim());
          currentField = '';
        } else if (ch === '\n') {
          currentRow.push(currentField.trim());
          currentField = '';
          if (currentRow.length > 1 || currentRow.some(f => f !== '')) {
            allRows.push(currentRow);
          }
          currentRow = [];
        } else {
          currentField += ch;
        }
      }
    }
    // Flush last row
    if (currentField || currentRow.length > 0) {
      currentRow.push(currentField.trim());
      if (currentRow.some(f => f !== '')) allRows.push(currentRow);
    }

    if (allRows.length === 0) return { headers: [], rawHeaders: [], remapped: [], extraHeaders: [], rows: [] };

    const rawHeaders = allRows[0];
    const canonicalHeaders = rawHeaders.map(h => canonicalise(h) || h);
    const remapped = rawHeaders
      .map((raw) => ({ raw, canonical: canonicalise(raw) }))
      .filter(({ raw, canonical }) => canonical && canonical !== raw);
    const extraHeaders = rawHeaders.filter(h => !canonicalise(h));

    const dataRows = [];
    for (let i = 1; i < allRows.length; i++) {
      const values = allRows[i];
      if (values.every(v => v === '')) continue;
      const row = {};
      canonicalHeaders.forEach((h, idx) => { row[h] = values[idx] !== undefined ? values[idx] : ''; });
      dataRows.push(row);
    }

    return { headers: canonicalHeaders, rawHeaders, remapped, extraHeaders, rows: dataRows };
  };
  // ──────────────────────────────────────────────────────────────────────────

  const loadCurrentStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const response = await authAxios.get('/admin/jobs/import/status');
      setCurrentStatus(response.data);
    } catch (error) {
      console.error('Failed to load job status:', error);
      toast.error('Failed to load current job status');
    } finally {
      setLoadingStatus(false);
    }
  }, [authAxios]);

  useEffect(() => {
    loadCurrentStatus();
  }, [loadCurrentStatus]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setSelectedFile(file);
    setPreview(null);
    setParsedData(null);
    setImportResult(null);
    setImportProgress(null);
    setLoadingPreview(true);

    try {
      // Parse CSV client-side (avoids proxy size limits)
      const text = await file.text();
      const { headers, remapped, extraHeaders, rows } = parseCSV(text);

      const salaryCount = rows.filter(r => r['Salary min'] && r['Salary min'] !== '').length;
      const companies = new Set(rows.map(r => r['Company Name']).filter(Boolean));
      const categories = new Set(rows.map(r => r['Category name']).filter(Boolean));

      const stats = {
        jobs_with_salary: salaryCount,
        unique_companies: companies.size,
        unique_categories: categories.size,
      };

      setParsedData({ columns: headers, rows, stats });

      // Send just headers + sample rows to backend for validation
      const response = await authAxios.post('/admin/jobs/import/preview-json', {
        columns: headers,
        sample_rows: rows.slice(0, 5),
        total_rows: rows.length,
        stats,
      });
      setPreview({ ...response.data, remapped, extraHeaders });
    } catch (error) {
      console.error('Failed to preview file:', error);
      toast.error('Failed to preview CSV: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !parsedData) return;

    const confirmed = window.confirm(
      `This will DELETE all ${currentStatus?.current_job_count || 0} existing jobs and all existing match results, then import ${parsedData.rows.length} new jobs. You will need to re-run the matching job afterward.\n\nThis action cannot be undone. Continue?`
    );
    if (!confirmed) return;

    setImporting(true);
    setImportResult(null);
    setImportProgress({ current: 0, total: parsedData.rows.length, pct: 0 });

    try {
      const BATCH_SIZE = 750;
      const rows = parsedData.rows;
      const totalBatches = Math.ceil(rows.length / BATCH_SIZE);
      let totalInserted = 0;
      let deletedCount = 0;
      let deletedMatchCount = 0;

      for (let b = 0; b < totalBatches; b++) {
        const batchRows = rows.slice(b * BATCH_SIZE, (b + 1) * BATCH_SIZE);
        const isLast = b === totalBatches - 1;

        const response = await authAxios.post('/admin/jobs/import/execute-batch', {
          rows: batchRows,
          batch_number: b,
          total_rows: rows.length,
          delete_existing: true,
          is_last: isLast,
        });

        totalInserted += response.data.inserted_in_batch || 0;
        if (b === 0) {
          deletedCount = response.data.deleted_count || 0;
          deletedMatchCount = response.data.deleted_match_count || 0;
        }

        const inserted = Math.min((b + 1) * BATCH_SIZE, rows.length);
        setImportProgress({
          current: inserted,
          total: rows.length,
          pct: Math.round((inserted / rows.length) * 100),
          batch: b + 1,
          totalBatches,
        });
      }

      const result = {
        success: true,
        rows_in_csv: rows.length,
        inserted_count: totalInserted,
        deleted_count: deletedCount,
        deleted_match_count: deletedMatchCount,
        errors: [],
      };
      setImportResult(result);
      toast.success(`Successfully imported ${totalInserted} jobs!`);
      loadCurrentStatus();
      setSelectedFile(null);
      setParsedData(null);
      setPreview(null);
    } catch (error) {
      console.error('Failed to import jobs:', error);
      toast.error('Failed to import jobs: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-8">
              <Link to="/" className="text-xl font-heading text-slate-900">
                Varyynt
              </Link>
              <nav className="flex items-center gap-1">
                <Link to="/admin" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Overview
                </Link>
                <Link to="/admin/candidates" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Candidates
                </Link>
                <Link to="/admin/job-analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Job Market
                </Link>
                <Link to="/admin/analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Analytics
                </Link>
                <Link to="/admin/market-pulse" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Market Pulse
                </Link>
                <Link to="/admin/messages" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Messages
                </Link>
                <Link to="/admin/support" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  User Support
                </Link>
                <Link to="/admin/job-import" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg font-semibold text-slate-900 bg-slate-100">
                  Job Import
                </Link>
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin/guide">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Admin Guide
                </Button>
              </Link>
              <Link to="/brand" target="_blank">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  Brand Guide
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Database className="h-8 w-8 text-lime-600" />
            <h1 className="text-2xl font-heading text-slate-900">Job Data Import</h1>
          </div>
          <p className="text-slate-500">Upload a new CSV file to replace all job listings in the database.</p>
        </div>

        {/* Step 1: Current Status */}
        <Card className="mb-6 border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-semibold text-slate-600">1</div>
              <CardTitle className="text-lg">Current Database Status</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {loadingStatus ? (
              <div className="flex items-center gap-2 text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading current status...
              </div>
            ) : currentStatus ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-2xl font-semibold text-slate-900">{currentStatus.current_job_count?.toLocaleString()}</p>
                    <p className="text-sm text-slate-500">Jobs currently in database</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadCurrentStatus}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
                {currentStatus.sample_jobs?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Recent jobs:</p>
                    <div className="space-y-2">
                      {currentStatus.sample_jobs.slice(0, 3).map((job, i) => (
                        <div key={i} className="text-sm text-slate-600 flex items-center gap-2">
                          <span className="truncate flex-1">{job.job_title}</span>
                          <span className="text-slate-400">at</span>
                          <span className="truncate">{job.company_name}</span>
                          <Badge variant="outline" className="text-xs">{formatDate(job.date_posted)}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-slate-500">Unable to load status</p>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Upload CSV */}
        <Card className="mb-6 border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-semibold text-slate-600">2</div>
                <CardTitle className="text-lg">Upload New Job Data</CardTitle>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadTemplate}
                data-testid="download-template-btn"
                className="text-slate-600 border-slate-300 hover:bg-slate-50"
              >
                <Download className="h-4 w-4 mr-1.5" />
                Download Template
              </Button>
            </div>
            <CardDescription className="mt-1">
              Required columns: Job title, Company Name, Description, Apply URL, Date posted, Category name. Extra columns in your file are automatically ignored — column names are case-insensitive.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  selectedFile ? 'border-lime-300 bg-lime-50' : 'border-slate-300 hover:border-slate-400'
                }`}
              >
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  {selectedFile ? (
                    <div className="space-y-2">
                      <FileSpreadsheet className="h-12 w-12 text-lime-600 mx-auto" />
                      <p className="font-medium text-slate-900">{selectedFile.name}</p>
                      <p className="text-sm text-slate-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      <Button variant="outline" size="sm" className="mt-2">
                        Choose Different File
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="h-12 w-12 text-slate-400 mx-auto" />
                      <p className="font-medium text-slate-700">Click to upload CSV file</p>
                      <p className="text-sm text-slate-500">or drag and drop</p>
                    </div>
                  )}
                </label>
              </div>

              {loadingPreview && (
                <div className="flex items-center justify-center gap-2 py-4 text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Analyzing file...
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Step 3: Preview */}
        {preview && (
          <Card className="mb-6 border-slate-200">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-semibold text-slate-600">3</div>
                <CardTitle className="text-lg">Import Preview</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-lime-50 border border-lime-200 rounded-lg text-center">
                    <p className="text-2xl font-semibold text-lime-700">{preview.row_count?.toLocaleString()}</p>
                    <p className="text-sm text-slate-600">Total Jobs</p>
                  </div>
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
                    <p className="text-2xl font-semibold text-blue-700">{preview.stats?.unique_companies?.toLocaleString()}</p>
                    <p className="text-sm text-slate-600">Companies</p>
                  </div>
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg text-center">
                    <p className="text-2xl font-semibold text-purple-700">{preview.stats?.unique_categories}</p>
                    <p className="text-sm text-slate-600">Categories</p>
                  </div>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-center">
                    <p className="text-2xl font-semibold text-amber-700">{preview.stats?.jobs_with_salary?.toLocaleString()}</p>
                    <p className="text-sm text-slate-600">With Salary</p>
                  </div>
                </div>

                {/* Validation */}
                <div className="p-4 rounded-lg border">
                  {preview.ready_to_import ? (
                    <div className="flex items-center gap-2 text-lime-700">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">File is valid and ready to import</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-amber-700">
                        <AlertCircle className="h-5 w-5" />
                        <span className="font-medium">Missing required columns</span>
                      </div>
                      <div className="text-sm text-slate-600">
                        {preview.missing_columns?.map((col, i) => (
                          <Badge key={i} variant="outline" className="mr-1 mb-1">{col}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Column remapping notices */}
                {preview.remapped?.length > 0 && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-800 mb-1.5">Column names auto-corrected:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {preview.remapped.map((r, i) => (
                        <span key={i} className="text-xs bg-white border border-blue-200 text-blue-700 px-2 py-0.5 rounded-full">
                          "{r.raw}" → "{r.canonical}"
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Extra/ignored columns */}
                {preview.extraHeaders?.length > 0 && (
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <p className="text-sm font-medium text-slate-600 mb-1.5">
                      {preview.extraHeaders.length} column{preview.extraHeaders.length > 1 ? 's' : ''} not recognised and will be ignored:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {preview.extraHeaders.map((col, i) => (
                        <Badge key={i} variant="outline" className="text-slate-500 text-xs">{col}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sample Data */}
                {preview.sample_data?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Sample data (first 3 rows):</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-3 text-slate-500">Job Title</th>
                            <th className="text-left py-2 px-3 text-slate-500">Company</th>
                            <th className="text-left py-2 px-3 text-slate-500">Category</th>
                            <th className="text-left py-2 px-3 text-slate-500">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.sample_data.slice(0, 3).map((row, i) => (
                            <tr key={i} className="border-b border-slate-100">
                              <td className="py-2 px-3 truncate max-w-[200px]">{row['Job title']}</td>
                              <td className="py-2 px-3 truncate max-w-[150px]">{row['Company Name']}</td>
                              <td className="py-2 px-3 truncate max-w-[150px]">{row['Category name']}</td>
                              <td className="py-2 px-3">{row['Date posted']}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Execute Import */}
        {preview?.ready_to_import && (
          <Card className="mb-6 border-lime-200 bg-gradient-to-br from-lime-50 to-white">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-lime-600 flex items-center justify-center text-sm font-semibold text-white">4</div>
                <CardTitle className="text-lg">Execute Import</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-white rounded-lg border border-lime-200">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-slate-700">
                        <Trash2 className="h-4 w-4 text-red-500" />
                        <span>Delete {currentStatus?.current_job_count?.toLocaleString()} jobs + all match results</span>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-slate-700">
                        <Upload className="h-4 w-4 text-lime-600" />
                        <span>Import {preview.row_count?.toLocaleString()} new jobs</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">
                    This will replace all existing job data. Make sure you have a backup if needed.
                  </p>
                  <Button 
                    onClick={handleImport}
                    disabled={importing}
                    className="bg-lime-600 hover:bg-lime-700"
                    data-testid="execute-import-btn"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {importProgress ? `Importing batch ${importProgress.batch}/${importProgress.totalBatches} (${importProgress.pct}%)` : 'Preparing...'}
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Delete & Import Jobs
                      </>
                    )}
                  </Button>
                </div>

                {/* Progress Bar */}
                {importing && importProgress && (
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Uploading {importProgress.current.toLocaleString()} of {importProgress.total.toLocaleString()} rows…</span>
                      <span>{importProgress.pct}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-lime-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${importProgress.pct}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Import Result */}
        {importResult && (
          <Card className={`mb-6 ${importResult.success ? 'border-lime-200 bg-lime-50' : 'border-red-200 bg-red-50'}`}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                {importResult.success ? (
                  <CheckCircle className="h-8 w-8 text-lime-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-8 w-8 text-red-600 flex-shrink-0" />
                )}
                <div className="space-y-2">
                  <h3 className={`font-semibold text-lg ${importResult.success ? 'text-lime-700' : 'text-red-700'}`}>
                    {importResult.success ? 'Import Successful!' : 'Import Completed with Errors'}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Jobs Deleted</p>
                      <p className="font-semibold">{importResult.deleted_count?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Matches Cleared</p>
                      <p className="font-semibold">{importResult.deleted_match_count?.toLocaleString() || '0'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Jobs Imported</p>
                      <p className="font-semibold">{importResult.inserted_count?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Final Count</p>
                      <p className="font-semibold">{importResult.final_job_count?.toLocaleString()}</p>
                    </div>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div className="mt-2 p-2 bg-white rounded border text-sm text-red-600">
                      {importResult.errors.slice(0, 3).map((err, i) => (
                        <p key={i}>{err}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Text */}
        <div className="text-sm text-slate-500 space-y-2">
          <p><strong>Note:</strong> Importing new jobs will also clear all existing match results. After importing, re-run the V-FIT matching job from the Admin Overview page to generate new match scores for users.</p>
          <p><strong>CSV Format:</strong> The CSV should have columns: Job title, Company Name, Job location, Office location, Description, Apply URL, Salary min, Salary maximum, Date posted (MM/DD/YYYY), Category name</p>
        </div>
      </main>
    </div>
  );
}
